import { type EnergyType } from "@/features/subscription/types/energyType"
import { type SalesChannelWithTelesales } from "@/scalars/salesChannelChoices"

/**
 * Configuration object driving the onboarding maintenance alert.
 *
 * The alert can be controlled in two ways:
 * - On/off: set `enabled` to toggle the alert unconditionally.
 * - Maintenance window: additionally provide a `maintenanceWindow` so the alert
 *   only shows during that period (while `enabled` is `true`).
 *
 * Display can additionally be narrowed to specific energies and/or sales
 * channels via `energyTypes` / `salesChannels` (see below).
 *
 * All wordings can be overridden from the flag; when a wording is omitted the
 * component falls back to the `OnboardingMaintenanceAlert` message catalog.
 */
export type OnboardingMaintenanceAlertConfig = {
    /** Master switch. When `false`, the alert is never displayed. */
    enabled: boolean
    /**
     * Optional visibility window. When provided (and `enabled` is `true`), the
     * alert only shows within it. When omitted, `enabled` alone controls display.
     *
     * `start`/`end` are ISO 8601 datetime strings including a timezone offset,
     * e.g. `2026-09-11T18:00:00+02:00`.
     */
    maintenanceWindow?: {
        start: string
        end: string
    }
    /**
     * Restrict display to subscriptions including at least one of these
     * energies. When omitted, the alert is not filtered by energy.
     *
     * e.g. `["gas"]` shows the alert only when gas is part of the selection.
     */
    energyTypes?: EnergyType[]
    /**
     * Restrict display to these sales channels. When omitted, the alert is not
     * filtered by sales channel.
     *
     * e.g. `["DIRECT", "TELESALES"]`.
     */
    salesChannels?: SalesChannelWithTelesales[]
    /** Optional wording overrides. Falls back to the message catalog when omitted. */
    wordings?: {
        title?: string
        /**
         * Rich text using the same next-intl tags as the message catalog:
         * - `<b>...</b>` for bold text
         * - `<br></br>` for a line break
         * - `<typeform>...</typeform>` for a link (target defaults to the onboarding error
         *   Typeform, or `descriptionLinkHref` when provided)
         *
         * e.g. `"En raison d'une maintenance <b>ce week-end</b>, <typeform>contactez-nous</typeform>."`
         */
        description?: string
        /** Optional destination for the `<typeform>` tag in `description`. */
        typeformUrl?: string
    }
}

export const ONBOARDING_MAINTENANCE_ALERT_DEFAULT: OnboardingMaintenanceAlertConfig = {
    enabled: false,
}
